import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { forgotPassword } from "@/services/auth";

interface ResetPasswordFormProps {
  onNext: (email: string) => void; // callback to go to "Check Email" screen with email
  onBack?: () => void; // callback to go back to login screen
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validation function
const validateEmail = (email: string): string | undefined => {
  if (!email.trim()) {
    return "Email is required";
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return "Please enter a valid email address";
  }
  return undefined;
};

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onNext, onBack }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setError(null); // Clear error when user types
    
    // Real-time validation if field has been touched
    if (touched) {
      const emailError = validateEmail(newEmail);
      setError(emailError || null);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    const emailError = validateEmail(email);
    setError(emailError || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email before proceeding
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      setTouched(true);
      return;
    }
    
    setError(null);
    setIsLoading(true);
    
    try {
      await forgotPassword(email.trim());
      onNext(email.trim());
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || "Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form
        className="flex flex-col gap-6 mt-8"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">Forgot Your Password?</h1>
          <p className="text-muted-foreground text-sm text-balance">
            Don&apos;t worry, we will send you instructions to reset your password.
          </p>
        </div>
        <div className="grid gap-6">
          <div className="grid gap-3">
            <Label htmlFor="email">Enter Email ID</Label>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleBlur}
              className={`h-11 transition-all duration-200 focus:ring-2 ${
                touched && error
                  ? 'border-destructive focus:ring-destructive/20'
                  : 'focus:ring-primary/20'
              }`}
              disabled={isLoading}
              required
              aria-invalid={touched && !!error}
              aria-describedby={touched && error ? 'email-error' : undefined}
            />
            {touched && error && (
              <div 
                id="email-error"
                className="flex items-center gap-1.5 text-sm text-destructive animate-in fade-in-0 slide-in-from-top-1"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
          <Button
            type="submit"
            className="h-12"
            variant="primary"
            disabled={isLoading || (touched && !!error)}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </form>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center justify-center gap-2 mt-4 text-primary hover:underline transition-colors"
        disabled={isLoading}
      >
        <ArrowLeft className="h-4 w-4"/>
        <span>Back to Login Page</span>
      </button>
    </>
  );
};

export default ResetPasswordForm;
